import { useCallback } from 'react';
import {
  createFileRoute,
  Outlet,
  useMatchRoute,
  useNavigate,
} from '@tanstack/react-router';
import { HeaderDashboard } from '@/components/shared/header-dashboard';
import { OpportunitiesListCard } from '@/features/cleaner/opportunities-list-card';
import { cn } from '@/lib/utils';

type BookingsTab = 'upcoming' | 'history' | 'all';

export const Route = createFileRoute(
  '/(cleaner)/_authenticated/cleaner/opportunities'
)({
  component: BookingsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    status: ['upcoming', 'history', 'all'].includes(search.status as string)
      ? (search.status as BookingsTab)
      : 'all',
  }),
});

function BookingsPage() {
  const matchRoute = useMatchRoute();
  const navigate = useNavigate();
  const { status: urlStatus } = Route.useSearch();
  const tab: BookingsTab =
    urlStatus && ['upcoming', 'history', 'all'].includes(urlStatus)
      ? urlStatus
      : 'upcoming';
  const isDetailPage = matchRoute({
    to: '/cleaner/opportunities/$id',
  });

  const handleTabChange = useCallback(
    (newTab: BookingsTab) => {
      navigate({
        to: '/cleaner/opportunities',
        search: { status: newTab },
        replace: true,
      });
    },
    [navigate]
  );

  return (
    <div>
      {!isDetailPage && (
        <>
          <HeaderDashboard>
            <HeaderDashboard.Title>Opportunities</HeaderDashboard.Title>
            <HeaderDashboard.Action>
              <div className="gap-2 flex items-center">
                <span className="text-sm mr-3">Sort by</span>
                <button
                  type="button"
                  onClick={() => handleTabChange('all')}
                  className={cn(
                    'text-sm font-medium transition-colors cursor-pointer border-1 py-2 px-3 rounded-md border-neutral-700 hover:bg-orange-500',
                    tab === 'all'
                      ? 'text-white bg-orange-500 border-0'
                      : 'text-gray-900 dark:text-gray-300 hover:text-[#FF783A]'
                  )}
                >
                  Date
                </button>

                <button
                  type="button"
                  onClick={() => handleTabChange('upcoming')}
                  className={cn(
                    'text-sm font-medium transition-colors cursor-pointer border-1 py-2 px-3 rounded-md border-neutral-700 hover:bg-orange-500',
                    tab === 'upcoming'
                      ? 'text-white bg-orange-500 border-0'
                      : 'text-gray-900 dark:text-gray-300 hover:text-[#FF783A]'
                  )}
                >
                  Price
                </button>

                <button
                  type="button"
                  onClick={() => handleTabChange('history')}
                  className={cn(
                    'text-sm font-medium transition-colors cursor-pointer border-1 py-2 px-3 rounded-md border-neutral-700 hover:bg-orange-500',
                    tab === 'history'
                      ? 'text-white bg-orange-500 border-0'
                      : 'text-gray-900 dark:text-gray-300 hover:text-[#FF783A]'
                  )}
                >
                  Nearby
                </button>
              </div>
            </HeaderDashboard.Action>
          </HeaderDashboard>
          <div className="border-b border-gray-200 dark:border-gray-700 w-full mb-6" />
        </>
      )}
      <Outlet />
      {!isDetailPage && <OpportunitiesListCard activeTab={tab} />}
    </div>
  );
}
